import { onUnmounted, Ref, ref, shallowRef, watch } from '@vue/composition-api'
import * as Comlink from 'comlink'
import type { WrapIframeOptions } from '../types/iframe'

export interface UseChildIframeApiReturn<T> {
  /**
   * 包装后的代理对象（使用 shallowRef 避免深度响应式）
   */
  proxy: Ref<Comlink.Remote<T> | null>
  /**
   * 是否已就绪
   */
  isReady: Ref<boolean>
  /**
   * 手动终止通信
   */
  terminate: () => void
}

/**
 * 包装 iframe 子应用暴露的 API
 * 用于父应用调用 iframe 子应用的方法
 *
 * @param iframeRef - iframe 元素的 ref
 * @param options - 配置选项
 *
 * @example
 * ```ts
 * interface ChildAPI {
 *   refresh(): Promise<void>
 * }
 *
 * const iframeRef = ref<HTMLIFrameElement | null>(null)
 * const { proxy, isReady } = useChildIframeApi<ChildAPI>(iframeRef)
 *
 * watchEffect(async () => {
 *   if (isReady.value && proxy.value) {
 *     await proxy.value.refresh()
 *   }
 * })
 * ```
 */
export function useChildIframeApi<T = Record<string, unknown>>(
  iframeRef: Ref<HTMLIFrameElement | null>,
  options?: WrapIframeOptions
): UseChildIframeApiReturn<T> {
  const proxy = shallowRef<Comlink.Remote<T> | null>(null)
  const isReady = ref(false)
  const targetOrigin = options?.targetOrigin || '*'

  watch(
    () => iframeRef.value,
    (iframe) => {
      if (iframe?.contentWindow) {
        // 等待 iframe 加载完成
        iframe.onload = () => {
          try {
            const contentWindow = iframe.contentWindow
            if (contentWindow) {
              const endpoint = Comlink.windowEndpoint(contentWindow, window, targetOrigin)
              proxy.value = Comlink.wrap<T>(endpoint)
              isReady.value = true
              options?.onReady?.()
              console.debug('[useChildIframeApi] Child iframe API wrapped successfully')
            }
          } catch (error) {
            console.error('[useChildIframeApi] Failed to wrap child iframe API:', error)
            options?.onError?.(error as Error)
          }
        }
      }
    },
    { immediate: true }
  )

  const terminate = () => {
    proxy.value = null
    isReady.value = false
    console.debug('[useChildIframeApi] Connection terminated')
  }

  onUnmounted(() => {
    terminate()
  })

  return {
    proxy,
    isReady,
    terminate,
  }
}
